import type {
  MarketplaceNearbyZonesInput,
  MarketplaceViewportAreaNameQuery,
} from './__generated__/viewport-area-name.zeus';
import type { GeoPoint } from './types';

const MAX_SEARCH_DISTANCE_METERS = 160_000;
/** Views up to this wide are named after the city at their centre; wider ones after their states. */
const CITY_VIEW_KM = 120;
const WIDE_VIEW_ZONES = 20;
/** A view up to this wide is covered by one lookup; a wider one samples along its longer side. */
const SAMPLE_SPACING_KM = 320;
/** Each sample is one Zeus request per map move, so a wide view takes few of them. */
const MAX_SAMPLES = 3;
const KM_PER_DEGREE = 111.32;

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'Washington, DC',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export const getViewportCenter = (vertices: GeoPoint[]): GeoPoint | null => {
  if (vertices.length === 0) {
    return null;
  }

  const latitudes = vertices.map(({ lat }) => lat);
  const longitudes = vertices.map(({ lon }) => lon);
  return {
    lat: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
    lon: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
  };
};

const isCityView = (spanKm: number) => spanKm <= CITY_VIEW_KM;

const getViewportBox = (vertices: GeoPoint[]) => {
  const latitudes = vertices.map(({ lat }) => lat);
  const longitudes = vertices.map(({ lon }) => lon);
  const south = Math.min(...latitudes);
  const north = Math.max(...latitudes);
  const west = Math.min(...longitudes);
  const east = Math.max(...longitudes);
  const heightKm = (north - south) * KM_PER_DEGREE;
  const widthKm = (east - west) * KM_PER_DEGREE * Math.cos((((south + north) / 2) * Math.PI) / 180);
  return { south, north, west, east, widthKm, heightKm };
};

/** The longer side of the view, in km; 0 for an empty one. */
export const getViewportSpanKm = (vertices: GeoPoint[]): number => {
  if (vertices.length === 0) return 0;
  const { widthKm, heightKm } = getViewportBox(vertices);
  return Math.max(widthKm, heightKm);
};

/** One sample for a view a lookup covers, else three, so the list always has a middle. */
const sampleCount = (spanKm: number) => (spanKm <= SAMPLE_SPACING_KM ? 1 : MAX_SAMPLES);

/** Evenly spread fractions of a side, each at the middle of its share. */
const sampleFractions = (count: number) =>
  Array.from({ length: count }, (_, index) => (index + 0.5) / count);

/**
 * Where to look up cities for a view. A city view looks at its centre; a wide
 * view looks along its longer side, so the states across it are seen. The
 * middle point is always the middle of the list.
 */
export const getViewportSamplePoints = (vertices: GeoPoint[]): GeoPoint[] => {
  const center = getViewportCenter(vertices);
  if (!center) return [];
  const box = getViewportBox(vertices);
  const spanKm = Math.max(box.widthKm, box.heightKm);
  if (isCityView(spanKm)) return [center];
  const alongWidth = box.widthKm >= box.heightKm;
  return sampleFractions(sampleCount(spanKm)).map((fraction) => ({
    lat: alongWidth ? center.lat : box.south + (box.north - box.south) * fraction,
    lon: alongWidth ? box.west + (box.east - box.west) * fraction : center.lon,
  }));
};

/** A wide view's lookups reach to the edge of each sample's share of it, up to the server's limit. */
const wideViewReachMeters = (spanKm: number) =>
  Math.min(MAX_SEARCH_DISTANCE_METERS, Math.round((spanKm / (2 * sampleCount(spanKm))) * 1000));

/**
 * A tight view asks for the one city at its centre. A wide view asks for the
 * cities around each sample point, so the name can say which states it covers.
 */
export const getViewportAreaNameInput = (
  { lat, lon }: GeoPoint,
  spanKm = 0,
): MarketplaceNearbyZonesInput => ({
  latitude: lat,
  limit: isCityView(spanKm) ? 1 : WIDE_VIEW_ZONES,
  longitude: lon,
  searchDistanceMeters: isCityView(spanKm)
    ? MAX_SEARCH_DISTANCE_METERS
    : wideViewReachMeters(spanKm),
  types: ['CITY'],
});

const listStates = (states: string[], undercounted: boolean): string => {
  const named = states.slice(0, 2);
  const more = undercounted || states.length > named.length;
  if (!more) return named.join(' & ');
  return `${named.join(', ')} & more`;
};

/**
 * "Atlanta, GA" for a city view. A wide view is named after the states its
 * sampled cities fall in: the state under the middle of the view first, then
 * the states with the most cities, as "Georgia", "Georgia & Tennessee" or
 * "Georgia, Tennessee & more". A view too wide for its samples to cover
 * always says "& more", so the name never claims to be complete.
 */
export const getViewportAreaName = (
  responses: MarketplaceViewportAreaNameQuery[],
  spanKm = 0,
): string | null => {
  const zones = responses.flatMap((data) => data.marketplace.nearbyZones);
  const zone = zones[0];
  if (!zone) return null;
  if (isCityView(spanKm)) return [zone.name, zone.stateAbbr].filter(Boolean).join(', ');

  const counts = new Map<string, number>();
  for (const { stateAbbr } of zones) {
    if (stateAbbr) counts.set(stateAbbr, (counts.get(stateAbbr) ?? 0) + 1);
  }
  const middle = responses[Math.floor(responses.length / 2)]?.marketplace.nearbyZones[0]?.stateAbbr;
  const states = [...counts.entries()]
    .sort((a, b) => Number(b[0] === middle) - Number(a[0] === middle) || b[1] - a[1])
    .map(([abbr]) => STATE_NAMES[abbr] ?? abbr);
  if (states.length === 0) return [zone.name, zone.stateAbbr].filter(Boolean).join(', ');
  return listStates(states, spanKm > MAX_SAMPLES * SAMPLE_SPACING_KM);
};
